/**
 * The app's usable width, i.e. the viewport minus any strip the assistant drawer has
 * reserved. Anything portaled to <body> sizes or flips against the VIEWPORT by default,
 * which puts it under (or over) the drawer; those call sites want this instead.
 *
 * Reads the inline custom property rather than getComputedStyle: it is what the drawer
 * writes, and it resolves in jsdom, so the behaviour stays testable.
 */
export function pageRightInset(): number {
  if (typeof document === 'undefined') return 0;
  const raw = document.documentElement.style.getPropertyValue('--app-assistant-inset');
  const px = Number.parseFloat(raw);
  return Number.isFinite(px) ? px : 0;
}

/**
 * The strip the assistant reserves along the BOTTOM edge — the closed launcher pill, which the
 * right inset does not model because the drawer is shut. Same inline-custom-property read as
 * pageRightInset, for the same reason.
 */
export function pageBottomInset(): number {
  if (typeof document === 'undefined') return 0;
  const raw = document.documentElement.style.getPropertyValue('--app-assistant-bottom-inset');
  const px = Number.parseFloat(raw);
  return Number.isFinite(px) ? px : 0;
}

export function pageWidth(): number {
  if (typeof window === 'undefined') return 0;
  return window.innerWidth - pageRightInset();
}

/**
 * Keeps an anchored popper out of the strips the assistant reserves: the open drawer on the
 * right, the closed launcher pill along the bottom. Not an offset — the trigger already moved
 * with the page; Radix just needs those edges as its collision boundary, or a menu opened near
 * one flips into it and renders underneath.
 */
export function pagePopperCollisionPadding() {
  return { right: pageRightInset(), bottom: pageBottomInset() };
}
